import { useEffect, useState } from "react";

import { Textfield, Button } from "@similarweb/ui-components";

import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { translate } from "app/filters/i18nFilter";
import type { TableSettingsField } from "app/pages/sales-intelligence/common-components/export-table/types";
import { getIntegrationNameFromUrl } from "app/pages/sales-intelligence/common-components/export-table/getIntegrationNameFromUrl";
import integrationTrackingService from "app/pages/sales-intelligence/services/tracking/integrationTrackingService";
import { WithKeysInput } from "app/pages/sales-intelligence/sub-modules/management/hocs/withKeysInput";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";

const INTEGRATION_TRANSLATION_PREFIX: Record<Integration, string> = {
    salesforce: "sf",
    hubspot: "hs",
    dynamics: "dynamics",
};

import { useAddFieldsModalProps } from "../../hooks/useAddFieldsModalProps";
import { useIsPredefinedOnCreateUpdateClaimEnabled } from "../../hooks/useIsPredefinedOnCreateUpdateClaimEnabled";
import { createSettings } from "../../utils/createSettings";
import {
    PRE_DEFINED_CREATE_FIELD,
    PRE_DEFINED_FIELD,
    PRE_DEFINED_UPDATE_FIELD,
    TECHNOGRAPHICS_FIELD,
} from "../../../../common-components/export-table/const";
import { getPredefinedFieldBase } from "../../../../common-components/export-table/utils";
import { FieldsTable } from "../FieldsTable/FieldsTable";

import {
    CUSTOM_ADD_FIELDS_MODAL_STYLES,
    StyledAmountSelected,
    StyledButtons,
    StyledFooter,
    StyledModalSubTitle,
    StyledModalTitle,
    StyledSearchField,
} from "./styles";
import type { AddFieldsModalProps } from "./type";

export const AddFieldsModal = ({ onClose, isOpen, exportName }: AddFieldsModalProps) => {
    const { integrationName } = getIntegrationNameFromUrl() as { integrationName: Integration };
    const isPredefinedOnCreateUpdateClaimEnabled = useIsPredefinedOnCreateUpdateClaimEnabled(integrationName);
    const settingsService = createSettings(integrationName, isPredefinedOnCreateUpdateClaimEnabled);

    const {
        addSettings,
        setNumOfAddedPreFields,
        setNumOfAddedPreDefinedCreateFields,
        setNumOfAddedPreDefinedUpdateFields,
        tableData,
        numOfAddedPreFields,
        numOfAddedPreDefinedCreateFields,
        numOfAddedPreDefinedUpdateFields,
        numOfAddedTechnographicsFields,
        setNumOfAddedTechnographicsFields,
    } = useAddFieldsModalProps(integrationName, exportName);

    const [numOfPreFields, setNumOfPreFields] = useState(1);
    const [numOfPreDefinedCreateFields, setNumOfPreDefinedCreateFields] = useState(1);
    const [numOfPreDefinedUpdateFields, setNumOfPreDefinedUpdateFields] = useState(1);
    const [numOfTechnographicsFields, setNumOfTechnographicsFields] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFields, setSelectedFields] = useState<string[]>([]);
    const [similarWebFields, setSimilarWebFields] = useState<TableSettingsField[]>([]);

    useEffect(() => {
        const availableFields = settingsService.getSettingsFilteredBySavedSetting(exportName);

        const swFields = settingsService.setupSelectedSwFields(availableFields, selectedFields);

        setSimilarWebFields(swFields.filter(filterBySimilarwebField));
    }, [searchTerm]);

    const handleClose = () => {
        setSelectedFields([]);
        setSearchTerm("");
        setNumOfPreFields(1);
        setNumOfPreDefinedCreateFields(1);
        setNumOfPreDefinedUpdateFields(1);
        setNumOfTechnographicsFields(1);
        onClose();
    };

    useEffect(() => {
        const availableFields = settingsService.getSettingsFilteredBySavedSetting(exportName);

        setSimilarWebFields(availableFields);

        return () => {
            setSimilarWebFields([]);
            setSelectedFields([]);
            setSearchTerm("");
            setNumOfPreFields(1);
            setNumOfPreDefinedCreateFields(1);
            setNumOfPreDefinedUpdateFields(1);
            setNumOfTechnographicsFields(1);
        };
    }, [tableData, isOpen]);

    const filterBySimilarwebField = ({ similarweb: { name } }: TableSettingsField) =>
        name.toLowerCase().includes(searchTerm.toLowerCase());

    const toggleSelectedField = (entry: TableSettingsField) => {
        if (selectedFields.includes(entry.similarweb.field)) {
            integrationTrackingService.trackRemoveField(exportName, entry.similarweb.field, integrationName);
            setSelectedFields(selectedFields.filter((selectedField) => entry.similarweb.field !== selectedField));
        } else {
            if (searchTerm) {
                integrationTrackingService.trackSearchAndAddField(exportName, entry.similarweb.field, integrationName);
            } else {
                integrationTrackingService.trackAddField(exportName, entry.similarweb.field, integrationName);
            }

            setSelectedFields([...selectedFields, entry.similarweb.field]);
        }
    };

    const handleAddFieldSelection = (
        item: TableSettingsField,
        field: string,
        count: number,
        setCount: (count: number) => void,
    ) => {
        const predefinedBase = getPredefinedFieldBase(item.similarweb.field);
        const isMatchingField = predefinedBase ? predefinedBase === field : item.similarweb.field.includes(field);

        if (isMatchingField && item.selected) {
            item.similarweb.field = `${item.similarweb.field}_modal_${count + 1}`;
            setCount(count + 1);
        }
    };

    const onAddField = (entry: TableSettingsField) => {
        const updatedSelectedSwFields = similarWebFields.map((item) => {
            if (item?.similarweb.field === entry?.similarweb.field) {
                item.selected = !item.selected;
                handleAddFieldSelection(item, PRE_DEFINED_FIELD, numOfAddedPreFields, setNumOfAddedPreFields);
                handleAddFieldSelection(
                    item,
                    PRE_DEFINED_CREATE_FIELD,
                    numOfAddedPreDefinedCreateFields,
                    setNumOfAddedPreDefinedCreateFields,
                );
                handleAddFieldSelection(
                    item,
                    PRE_DEFINED_UPDATE_FIELD,
                    numOfAddedPreDefinedUpdateFields,
                    setNumOfAddedPreDefinedUpdateFields,
                );
                handleAddFieldSelection(
                    item,
                    TECHNOGRAPHICS_FIELD,
                    numOfAddedTechnographicsFields,
                    setNumOfAddedTechnographicsFields,
                );
                return { ...item };
            }
            return item;
        });

        setSimilarWebFields(updatedSelectedSwFields);
        toggleSelectedField(entry);
    };

    const handlePreDefinedFieldChange = (fieldInputValue: number) => setNumOfPreFields(fieldInputValue);

    const handlePreDefinedCreateFieldChange = (fieldInputValue: number) =>
        setNumOfPreDefinedCreateFields(fieldInputValue);

    const handlePreDefinedUpdateFieldChange = (fieldInputValue: number) =>
        setNumOfPreDefinedUpdateFields(fieldInputValue);

    const handleTechnographicsFieldChange = (fieldInputValue: number) => setNumOfTechnographicsFields(fieldInputValue);

    const handleFieldCreation = (
        numOfFields: number,
        numOfAddedFields: number,
        setNumOfAddedFields: (numOfAddedFields: number) => void,
        field: string,
    ) => {
        const fields = [];
        if (Number(numOfFields) > 1) {
            for (let index = 1; index < Number(numOfFields); index++) {
                fields.push(`${field}_modal_${numOfAddedFields + index}`);
            }
            setNumOfAddedFields(numOfAddedFields + Number(numOfFields));
        }
        return fields;
    };

    const onClickAddFieldsToTable = () => {
        integrationTrackingService.trackAddFields(integrationName, exportName, selectedFields.length);

        const preFields = handleFieldCreation(
            numOfPreFields,
            numOfAddedPreFields,
            setNumOfAddedPreFields,
            PRE_DEFINED_FIELD,
        );
        const preDefinedCreateFields = handleFieldCreation(
            numOfPreDefinedCreateFields,
            numOfAddedPreDefinedCreateFields,
            setNumOfAddedPreDefinedCreateFields,
            PRE_DEFINED_CREATE_FIELD,
        );
        const preDefinedUpdateFields = handleFieldCreation(
            numOfPreDefinedUpdateFields,
            numOfAddedPreDefinedUpdateFields,
            setNumOfAddedPreDefinedUpdateFields,
            PRE_DEFINED_UPDATE_FIELD,
        );
        const technographicsFields = handleFieldCreation(
            numOfTechnographicsFields,
            numOfAddedTechnographicsFields,
            setNumOfAddedTechnographicsFields,
            TECHNOGRAPHICS_FIELD,
        );

        addSettings([
            ...preFields,
            ...preDefinedCreateFields,
            ...preDefinedUpdateFields,
            ...technographicsFields,
            ...selectedFields,
        ]);
        onClose();
    };

    const searchSimilarWebField = (search: string) => {
        integrationTrackingService.trackSearchSWField(integrationName, exportName, search);
        setSearchTerm(search);
    };

    const renderTable = () => (
        <FieldsTable
            onPreDefinedChange={handlePreDefinedFieldChange}
            onPreDefinedCreateChange={handlePreDefinedCreateFieldChange}
            onPreDefinedUpdateChange={handlePreDefinedUpdateFieldChange}
            onTechnographicsChange={handleTechnographicsFieldChange}
            onSelect={onAddField}
            numOfPreDefined={numOfPreFields}
            numOfPreDefinedCreate={numOfPreDefinedCreateFields}
            numOfPreDefinedUpdate={numOfPreDefinedUpdateFields}
            numOfTechnographics={numOfTechnographicsFields}
            data={similarWebFields}
            exportName={exportName}
        />
    );

    const renderSelectedItems = () => {
        const prefix =
            selectedFields.length === 0
                ? translate("si.management.sf.settings_export.modal.add_fields.no")
                : selectedFields.length;

        const value =
            selectedFields.length === 0 || selectedFields.length > 1
                ? translate("si.management.sf.settings_export.modal.add_fields.amount_selected")
                : translate("si.management.sf.settings_export.modal.add_field.amount_selected");

        return (
            <StyledAmountSelected hasValues={Boolean(selectedFields.length)}>
                {prefix}&nbsp;{value}
            </StyledAmountSelected>
        );
    };

    const isDisabled = !selectedFields.length || !numOfPreFields;

    return (
        <ProModal
            isOpen={isOpen}
            showCloseIcon={false}
            customStyles={CUSTOM_ADD_FIELDS_MODAL_STYLES}
        >
            <WithKeysInput
                isEnterDisabled={isDisabled}
                onEnter={onClickAddFieldsToTable}
                onEscape={handleClose}
            >
                <StyledModalTitle>
                    {translate("si.management.sf.settings_export.modal.add_fields.title")}
                </StyledModalTitle>
                <StyledModalSubTitle>
                    {translate(
                        `si.management.${INTEGRATION_TRANSLATION_PREFIX[integrationName]}.settings_export.modal.add_fields.sub_title`,
                    )}
                </StyledModalSubTitle>
                <StyledSearchField>
                    <Textfield
                        iconName="search"
                        onChange={searchSimilarWebField}
                        defaultValue={searchTerm}
                        dataAutomation="si-salesforce-export-search-field"
                        placeholder={translate("si.management.sf.settings_export.modal.add_fields.input.placeholder")}
                    />
                </StyledSearchField>
                {renderTable()}
                <StyledFooter>
                    {renderSelectedItems()}
                    <StyledButtons>
                        <Button
                            type="flat"
                            onClick={handleClose}
                        >
                            {translate("si.management.sf.settings_export.modal.add_fields.btn.cancel")}
                        </Button>
                        <Button
                            isDisabled={isDisabled}
                            type="primary"
                            onClick={onClickAddFieldsToTable}
                        >
                            {translate("si.management.sf.settings_export.modal.add_fields.btn.add")}
                        </Button>
                    </StyledButtons>
                </StyledFooter>
            </WithKeysInput>
        </ProModal>
    );
};
